import { startCase } from 'lodash'
import React, { useState } from 'react'
import { useInterval } from 'react-use'
import Picker from '@emoji-mart/react'

import { Input } from 'almanac-ui/TextInputField/Input'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { IconButton } from 'components/IconButton/IconButton'
import { Button } from 'components/Button'
import { BaseEmoji } from 'types'

import { voiceParams } from './Step2'

import styles from './Step3.module.scss'
import BaseStyles from 'components/BlazeCommandLine/BlazeCommandLine.module.scss'
import { TagInput } from 'components/TagInput/TagInput'
import { Heading } from 'blaze-ui/Heading/Heading'
import cx from 'classnames'
import { titleCase } from 'utils'

export type Step3Props = {
  voiceParams: voiceParams
  onChange: (vp: voiceParams) => void
  editMode?: boolean
}

const orderedKeys = ['purpose', 'audience', 'tone', 'emotions', 'character', 'syntax', 'language'] as const

export const Step3: React.FC<React.PropsWithChildren<Step3Props>> = ({ voiceParams, onChange, editMode }) => {
  const [voiceName, setVoiceName] = useState(voiceParams.name)
  const [voiceNameAnimation, setVoiceNameAnimation] = useState('')
  const [playing, setPlaying] = useState(!editMode)
  const [emojiMenuOpen, setEmojiMenuOpen] = useState(false)

  useInterval(
    () => {
      const newStr = voiceNameAnimation + voiceName[voiceNameAnimation.length]
      if (newStr === voiceName) {
        setPlaying(false)
      }
      setVoiceNameAnimation(newStr)
    },
    playing ? 50 : null,
  )

  const handleParamChange = (k: keyof Omit<voiceParams, 'name'>) => (tags: string[]) => {
    onChange({ ...voiceParams, [k]: tags, name: voiceName })
  }

  const textTags = ['audience', 'purpose']

  return (
    <div className={styles.Step3}>
      <div className={BaseStyles.section}>
        <Heading level={4}>Voice Name</Heading>
        <div className={styles.voiceNameAndEmojiContainer}>
          <DropdownTrigger forceOpen={emojiMenuOpen} onClose={() => setEmojiMenuOpen(false)} placement="bottom-start">
            <IconButton size={'lg'} variant="secondary" aria-label="Emoji">
              {voiceParams.emoji ? (
                <div className={styles.emoji}>{voiceParams.emoji}</div>
              ) : (
                // flower emoji
                '\u{1F33C}'
              )}
            </IconButton>
            {(close) => (
              <div className={styles.pickerContainer}>
                <Picker
                  theme="light"
                  autoFocus
                  previewPosition="none"
                  onEmojiSelect={async (emoji: BaseEmoji) => {
                    onChange({ ...voiceParams, emoji: emoji.native })
                    close()
                  }}
                />
                <div className={styles.pickerTopBar}>
                  <Button
                    isDisabled={!voiceParams.emoji}
                    onPress={() => {
                      onChange({ ...voiceParams, emoji: null })
                      close()
                    }}
                    size="sm"
                    variant="secondaryGrey"
                  >
                    Remove Emoji
                  </Button>
                </div>
              </div>
            )}
          </DropdownTrigger>
          <Input
            placeholder="e.g. Blaze Social Media"
            value={playing ? voiceNameAnimation : voiceName}
            autoFocus
            aria-label="Voice Name"
            name="voiceName"
            size="lg"
            onChange={(e) => {
              onChange({ ...voiceParams, name: e.currentTarget.value })
              setVoiceName(e.currentTarget.value)
            }}
          />
        </div>
      </div>

      <div className={BaseStyles.section}>
        <Heading level={4}>Modify your brand voice</Heading>
        <div className={styles.parameters}>
          {orderedKeys.map((k, i) => {
            const v = voiceParams[k]

            return (
              <React.Fragment key={k}>
                <div className={styles.wrapper}>
                  <label className={cx(styles.label, { [styles.start]: textTags.includes(k) })} htmlFor={k}>
                    {startCase(k.endsWith('s') ? k.slice(0, -1) : k)}
                  </label>
                  {textTags.includes(k) ? (
                    <Input
                      isTextarea
                      name={k}
                      id={k}
                      value={v.join(', ')}
                      className={styles.textbox}
                      rows={2}
                      onChange={(e) => handleParamChange(k as keyof Omit<voiceParams, 'name'>)([e.currentTarget.value])}
                    />
                  ) : (
                    <div className={styles.tagInputContainer}>
                      <TagInput
                        aria-label={titleCase(k)}
                        key={k}
                        maxWidth
                        tags={Array.isArray(v) ? v : [v]}
                        onChange={handleParamChange(k as keyof Omit<voiceParams, 'name'>)}
                      />
                    </div>
                  )}
                </div>
              </React.Fragment>
            )
          })}
        </div>
      </div>
    </div>
  )
}
